import { Button, IconButton } from '@mui/material';
import { FaPlus, FaTrash } from 'react-icons/fa';

import React, {useState, useEffect, useRef} from 'react'
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import apiClient from "../axios/axiosInterceptor";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Tab, Tabs,
    Typography
} from '@mui/material'

import CustomTabPanel from '../shared/CustomTabPanel'
import a11yProps from '../shared/a11yProps'
import DialogFormActions from '../shared/DialogFormActions'
import SpeechContext from './SpeechContext'

export default function RoleCreate({project, onClose, onSubmit}) {
    const parseSpeechContext = (speechContext) => {
        try {
            return JSON.parse(speechContext)
        } catch (e) {
            console.log("Failed to parsed speech context from backend. Speech context data: ", speechContext);
            return speechContext;
        }
    }

    const [name, setName] = useState(project ? project.name : '');
    const [background, setBackground] = useState(project ? project.background : true);
    const [fullDuplex, setFullDuplex] = useState(project ? project.fullDuplex : true);
    const [backgroundFiles, setBackgroundFiles] = useState([]);
    const [backgroundFileId, setBackgroundFileId] = useState(project ? project.backgroundFileId : '');

    const [removeNoise, setRemoveNoise] = useState(project ? project.removeNoise : '');
    const [backgroundVolume, setBackgroundVolume] = useState(project ? project.backgroundVolume : 50);
    const [voiceVolume, setVoiceVolume] = useState(project ? project.voiceVolume : 50);
    const [voiceRate, setVoiceRate] = useState(project ? project.voiceRate : 100);

    const [hardLimit, setHardLimit] = useState(project ? project.hardLimit : '');
    const [languages, setLanguages] = useState([]);
    const [languageId, setLanguageId] = useState(project ? project.languageId : '');

    const [sttServiceProviders, setSttServiceProviders] = useState([]);
    const [sttServiceProviderId, setSttServiceProviderId] = useState(project ? project.sttServiceProviderId : '');

    const [speechModels, setSpeechModels] = useState([]);
    const [speechModelId, setSpeechModelId] = useState(project ? project.speechModelId : '');

    const [ttsLanguageLocales, setTtsLanguageLocales] = useState([]);
    const [languageLocaleId, setLanguageLocaleId] = useState(project ? project.languageLocaleId : '');

    const [voices, setVoices] = useState([]);
    const [voiceId, setVoiceId] = useState(project ? project.voiceId : '');

    const [utteranceDetection, setUtteranceDetection] = useState(project ? project.utteranceDetection : 50);
    const [speechContext, setSpeechContext] = useState(project ? parseSpeechContext(project.speechContext) : []);

    const [vadThresholdStart, setVadThresholdStart] = useState(project ? project.vadThresholdStart : 0);
    const [vadThresholdStop, setVadThresholdStop] = useState(project ? project.vadThresholdStop : 0);

    const [playbackVadThresholdStart, setPlaybackVadThresholdStart] = useState(project ? project.playbackVadThresholdStart : 0);
    const [playbackVadThresholdStop, setPlaybackVadThresholdStop] = useState(project ? project.PlaybackVadThresholdStop : 0);

    const [volumeThreshold, setVolumeThreshold] = useState(project ? project.volumeThreshold : -60);
    const [dynamicVolumeReduction, setDynamicVolumeReduction] = useState(project ? project.dynamicVolumeReduction : -22);

    const [tabValue, setTabValue] = React.useState(0);
    const dialogRef = useRef(null);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await apiClient.get('/languages');
                setLanguages(response.data.list);
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };

        const fetchSttServiceProviders = async () => {
            try {
                const response = await apiClient.get('/sttServiceProviders');
                setSttServiceProviders(response.data.list);
            } catch (error) {
                console.error('Error fetching stt service providers:', error);
            }
        };

        const fetchSpeechModels = async () => {
            try {
                const response = await apiClient.get('/speechModels');
                setSpeechModels(response.data.list);
            } catch (error) {
                console.error('Error fetching speech models:', error);
            }
        };

        const fetchTtsLanguageLocales = async () => {
            try {
                const response = await apiClient.get('/languageLocales');
                setTtsLanguageLocales(response.data.list);
            } catch (error) {
                console.error('Error fetching tts language locales:', error);
            }
        }

        const fetchBackgroundFiles = async () => {
            try {
                const response = await apiClient.get('/backgroundFiles');
                setBackgroundFiles(response.data.list);
            } catch (error) {
                console.error('Error fetching tts language locales:', error);
            }
        }

        if (languageLocaleId) {
            handleLanguageLocaleChange({target: {value: languageLocaleId}});
        }

        fetchLanguages();
        fetchSttServiceProviders();
        fetchSpeechModels();
        fetchTtsLanguageLocales();
        fetchBackgroundFiles();
    }, []);

    const handleSubmit = e => {
        e.preventDefault();
        const formData = {
            name: name,
            background: background,
            backgroundFileId: backgroundFileId,
            backgroundVolume: backgroundVolume,

            fullDuplex: fullDuplex,
            removeNoise: removeNoise,
            voiceVolume: voiceVolume,
            voiceRate: voiceRate,
            hardLimit: hardLimit,

            languageId: languageId,
            sttServiceProviderId: sttServiceProviderId,
            speechModelId: speechModelId,
            voiceId: voiceId,
            utteranceDetection: utteranceDetection,

            volumeThreshold: volumeThreshold,
            dynamicVolumeReduction: dynamicVolumeReduction,
            speechContext: JSON.stringify(speechContext),
            vadThresholdStart: vadThresholdStart,
            vadThresholdStop: vadThresholdStop,
            playbackVadThresholdStart: playbackVadThresholdStart,
            playbackVadThresholdStop: playbackVadThresholdStop,
        };

        onSubmit(formData);
    };

    const handleLanguageLocaleChange = async (e) => {
        const selectedTtsLanguageId = parseInt(e.target.value);
        setLanguageLocaleId(selectedTtsLanguageId);

        if (selectedTtsLanguageId !== 0) {
            console.log('selectedTtsLanguageId:', selectedTtsLanguageId);
            try {
                const response = await apiClient.get(`/languageLocales/${selectedTtsLanguageId}/voices`);
                setVoices(response.data.list);
            } catch (error) {
                console.error('Error fetching speech models:', error);
            }
        } else {
            console.log('No call. selectedTtsLanguageId:', selectedTtsLanguageId);
            setVoices([]);
        }
    };

    const handleClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
        }
        onClose();
    };

    return (
        <React.Fragment>
            <Dialog
                open={true}
                onClose={handleClose}
                className="core-dialog core-dialog-scrollable"
            >
                <Box ref={dialogRef}>
                    <DialogTitle sx={{fontWeight: 'bold'}}>{project ? 'Edit' : 'Create'} Project</DialogTitle>
                    <form
                        onSubmit={handleSubmit}
                        style={{display: 'contents'}}
                        className="core-form"
                    >
                        <DialogContent className="core-dialog-content">
                            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                                <Tabs value={tabValue} onChange={handleTabChange} aria-label="project form tabs">
                                    <Tab label="Core Configuration" {...a11yProps(0, 'project-form-tabs')} />
                                    <Tab label="Advanced Configuration" {...a11yProps(1, 'project-form-tabs')} />
                                    <Tab label="Speech Context" {...a11yProps(1, 'project-form-tabs')} />
                                </Tabs>
                            </Box>
                            <CustomTabPanel value={tabValue} index={0} tabPanelPrefix="project-form-tabs">
                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <TextField
                                            autoFocus
                                            required
                                            margin="dense"
                                            id="name"
                                            label="Name"
                                            name="name"
                                            variant="standard"
                                            value={name}
                                            fullWidth
                                            onChange={e => setName(e.target.value)}
                                        />
                                    </div>

                                    <div className="core-form-row-item">
                                        <TextField
                                            autoFocus
                                            margin="dense"
                                            id="hardLimit"
                                            label="Hard Limit"
                                            name="hardLimit"
                                            fullWidth
                                            variant="standard"
                                            value={hardLimit}
                                            onChange={e => setHardLimit(parseInt(e.target.value))}
                                        />
                                    </div>
                                </Box>

                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <FormControlLabel
                                            label="Full Duplex"
                                            control={
                                                <Checkbox
                                                    id="fullDuplex"
                                                    name="fullDuplex"
                                                    checked={fullDuplex}
                                                    onChange={e => setFullDuplex(e.target.checked)}
                                                />
                                            }
                                        />
                                    </div>
                                    <div className="core-form-row-item">
                                        <FormControlLabel
                                            label="Remove Noise"
                                            control={
                                                <Checkbox
                                                    id="removeNoise"
                                                    name="removeNoise"
                                                    checked={removeNoise}
                                                    onChange={e => setRemoveNoise(e.target.checked)}
                                                />
                                            }
                                        />
                                    </div>
                                </Box>

                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <Typography>Voice Volume</Typography>
                                        <Slider
                                            min={0}
                                            max={100}
                                            value={voiceVolume}
                                            aria-label="Voice Volume"
                                            valueLabelDisplay="auto"
                                            onChange={e => setVoiceVolume(parseInt(e.target.value))}
                                        />
                                        <Typography>{voiceVolume}</Typography>
                                    </div>

                                    <div className="core-form-row-item">
                                        <Typography>Voice Rate</Typography>
                                        <Slider
                                            min={0}
                                            max={200}
                                            value={voiceRate}
                                            aria-label="Voice Volume"
                                            valueLabelDisplay="auto"
                                            onChange={e => setVoiceRate(parseInt(e.target.value))}
                                        />
                                        <Typography>{voiceRate}</Typography>
                                    </div>
                                </Box>

                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <FormControlLabel
                                            label="Background"
                                            control={
                                                <Checkbox
                                                    id="background"
                                                    name="background"
                                                    checked={background}
                                                    onChange={e => setBackground(e.target.checked)}
                                                />
                                            }
                                        />
                                    </div>
                                </Box>

                                {background && (
                                    <Box className="core-form-row" sx={{mb: 3}}>
                                        <div className="core-form-row-item">
                                            <FormControl fullWidth>
                                                <InputLabel id="backgroundFileLabel">Select a background
                                                    file</InputLabel>
                                                <Select
                                                    labelId="backgroundFileLabel"
                                                    id="backgroundFile"
                                                    value={backgroundFileId}
                                                    label="Background File"
                                                    onChange={e => setBackgroundFileId(parseInt(e.target.value))}
                                                    variant={'outlined'}
                                                >
                                                    {backgroundFiles.map(backgroundFile => (
                                                        <MenuItem
                                                            value={backgroundFile.id}>{backgroundFile.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <div className="core-form-row-item">
                                            <Typography>Background Volume</Typography>
                                            <Slider
                                                min={0}
                                                max={100}
                                                value={backgroundVolume}
                                                aria-label="Background Volume"
                                                valueLabelDisplay="auto"
                                                onChange={e => setBackgroundVolume(parseInt(e.target.value))}
                                            />
                                            <Typography>{backgroundVolume}</Typography>
                                        </div>
                                    </Box>
                                )}

                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <FormControl fullWidth>
                                            <InputLabel id="languageLabel">Select a language</InputLabel>
                                            <Select
                                                labelId="languageLabel"
                                                id="language"
                                                value={languageId}
                                                label="Language"
                                                onChange={e => setLanguageId(parseInt(e.target.value))}
                                                variant={'outlined'}
                                            >
                                                {languages.map(language => (
                                                    <MenuItem value={language.id}>{language.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <div className="core-form-row-item">
                                        <FormControl fullWidth>
                                            <InputLabel id="sttServiceProviderLabel">Select a STT service
                                                provider</InputLabel>
                                            <Select
                                                labelId="sttServiceProviderLabel"
                                                id="sttServiceProvider"
                                                value={sttServiceProviderId}
                                                label="Language"
                                                onChange={e => setSttServiceProviderId(parseInt(e.target.value))}
                                                variant={'outlined'}
                                            >
                                                {sttServiceProviders.map(sttServiceProvider => (
                                                    <MenuItem
                                                        value={sttServiceProvider.id}>{sttServiceProvider.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </Box>

                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <FormControl fullWidth>
                                            <InputLabel id="speechModelLabel">Select a speech model</InputLabel>
                                            <Select
                                                labelId="speechModelLabel"
                                                id="speechdModel"
                                                value={speechModelId}
                                                label="Speed Model"
                                                onChange={e => setSpeechModelId(parseInt(e.target.value))}
                                                variant={'outlined'}
                                            >
                                                {speechModels.map(speechModel => (
                                                    <MenuItem value={speechModel.id}>{speechModel.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <div className="core-form-row-item">
                                        <FormControl fullWidth>
                                            <InputLabel id="ttsLanguageLocaleLabel">Select a TTS language
                                                locale</InputLabel>
                                            <Select
                                                labelId="ttsLanguageLocaleLabel"
                                                id="ttsLanguageLocale"
                                                value={languageLocaleId}
                                                label="TTS Language Locale"
                                                onChange={handleLanguageLocaleChange}
                                                variant={'outlined'}
                                            >
                                                {ttsLanguageLocales.map(ttsLanguageLocale => (
                                                    <MenuItem
                                                        value={ttsLanguageLocale.id}>{ttsLanguageLocale.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </Box>

                                <Box className="core-form-row">
                                    <div className="core-form-row-item">
                                        <FormControl fullWidth>
                                            <InputLabel id="voiceLabel">Select a voice</InputLabel>
                                            <Select
                                                labelId="voiceLabel"
                                                id="voice"
                                                value={voiceId}
                                                label="Voice"
                                                onChange={e => setVoiceId(parseInt(e.target.value))}
                                                variant={'outlined'}>
                                                {voices.map(voice => (
                                                    <MenuItem value={voice.id}>{voice.friendlyName}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </Box>
                            </CustomTabPanel>
                            <CustomTabPanel value={tabValue} index={1} tabPanelPrefix="project-form-tabs">
                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <Typography>Utterance Detection</Typography>
                                        <Slider
                                            min={0}
                                            max={100}
                                            value={utteranceDetection}
                                            aria-label="Utterance Detection"
                                            valueLabelDisplay="auto"
                                            onChange={e => setUtteranceDetection(parseInt(e.target.value))}
                                        />
                                        <Typography>{utteranceDetection}</Typography>
                                    </div>
                                    <div className="core-form-row-item">
                                        <Typography>Volume Threshold</Typography>
                                        <Slider
                                            min={-100}
                                            max={100}
                                            value={volumeThreshold}
                                            aria-label="Volume Threshold"
                                            valueLabelDisplay="auto"
                                            onChange={e => setVolumeThreshold(parseInt(e.target.value))}
                                        />
                                        <Typography>{volumeThreshold}</Typography>
                                    </div>
                                </Box>
                                <Box className="core-form-row" sx={{mb: 3}}>
                                    <div className="core-form-row-item">
                                        <Typography>Dynamic Volume Reduction</Typography>
                                        <Slider
                                            min={-70}
                                            max={70}
                                            value={dynamicVolumeReduction}
                                            aria-label="Dynamic Volume Reduction"
                                            valueLabelDisplay="auto"
                                            onChange={e => setDynamicVolumeReduction(parseInt(e.target.value))}
                                        />
                                        <Typography>{dynamicVolumeReduction}</Typography>
                                    </div>
                                </Box>
                                <Box className="core-form-row">
                                    <div className="core-form-row-item">
                                        <Typography>Vad Threshold Start</Typography>
                                        <Slider
                                            min={0.0}
                                            max={1.0}
                                            step={0.1}
                                            value={vadThresholdStart}
                                            aria-label="Vad Threshold Start"
                                            valueLabelDisplay="auto"
                                            onChange={e => setVadThresholdStart(parseFloat(e.target.value))}
                                        />
                                        <Typography>{vadThresholdStart}</Typography>
                                    </div>
                                    <div className="core-form-row-item">
                                        <Typography>Vad Threshold Stop</Typography>
                                        <Slider
                                            min={0.0}
                                            max={1.0}
                                            step={0.1}
                                            value={vadThresholdStop}
                                            aria-label="Vad Threshold Stop"
                                            valueLabelDisplay="auto"
                                            onChange={e => setVadThresholdStop(parseFloat(e.target.value))}
                                        />
                                        <Typography>{vadThresholdStop}</Typography>
                                    </div>
                                </Box>
                                <Box className="core-form-row">
                                    <div className="core-form-row-item">
                                        <Typography>Playback Vad Threshold Start</Typography>
                                        <Slider
                                            min={0.0}
                                            max={1.0}
                                            step={0.1}
                                            value={playbackVadThresholdStart}
                                            aria-label="Playback Vad Threshold Start"
                                            valueLabelDisplay="auto"
                                            onChange={e => setPlaybackVadThresholdStart(parseFloat(e.target.value))}
                                        />
                                        <Typography>{playbackVadThresholdStart}</Typography>
                                    </div>
                                    <div className="core-form-row-item">
                                        <Typography>Playback Vad Threshold Stop</Typography>
                                        <Slider
                                            min={0.0}
                                            max={1.0}
                                            step={0.1}
                                            value={playbackVadThresholdStop}
                                            aria-label="Playback Vad Threshold Stop"
                                            valueLabelDisplay="auto"
                                            onChange={e => setPlaybackVadThresholdStop(parseFloat(e.target.value))}
                                        />
                                        <Typography>{playbackVadThresholdStop}</Typography>
                                    </div>
                                </Box>
                            </CustomTabPanel>
                            <CustomTabPanel index={tabValue} value={2} tabPanelPrefix="project-form-tabs">
                                <SpeechContext
                                  speechContext={speechContext}
                                  setSpeechContext={setSpeechContext}
                                />
                            </CustomTabPanel>
                        </DialogContent>
                        <DialogFormActions dialogRef={dialogRef} onClose={onClose}></DialogFormActions>
                    </form>
                </Box>
            </Dialog>
        </React.Fragment>
    )
}
